import { Box, Button } from '@inflearn/ds-react-next';
import { useLocalStorage } from '@mantine/hooks';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { fetchClientApiV1RoadmapAuthorUserId } from '../../../../resources/@types/courseSchema';
import { logger } from '../../../../resources/Logger';
import EmptyStates from '../../../components/common/EmptyStates';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import GridList from '../../../components/pages/profile/GridList/GridList';
import ImpressionArea from '../../../components/pages/profile/ImpressionArea/ImpressionArea';
import ReFetchInfinityScroll from '../../../components/pages/profile/ReFetchInfinityScroll/ReFetchInfinityScroll';
import RoadmapItem from '../../../components/pages/profile/RoadmapItem/RoadmapItem';
import RoadmapSkeletonItem from '../../../components/pages/profile/RoadmapItem/RoadmapSkeletonItem';
import SectionHeader from '../../../components/pages/profile/SectionHeader/SectionHeader';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { STORAGE_KEY, STORAGE_VALUE } from '../../../constants/storage';
import { INFLEARN_URL } from '../../../constants/urls';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import {
  getUseRoadmapAuthorUserIdInfinityQueryKey,
  getUseRoadmapAuthorUserIdQueryUrl,
  useRoadmapAuthorUserIdInfinityQuery,
} from '../../../queries/useRoadmapAuthorUserIdQuery';
import {
  type WithCommonNextPage,
  withProfileUserProps,
} from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';

const ROADMAP_ITEM_PAGE_LIMIT = 12;

const UserRoadmapsPage: WithCommonNextPage = () => {
  const { t } = useTranslation();
  const { isMe } = useCheckMyProfile();

  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });

  const [viewRoadmap, setViewRoadmap] = useLocalStorage({
    key: STORAGE_KEY.VIEW_USER_ROADMAP,
    getInitialValueInEffect: false,
  });

  const { data, isFetchingNextPage, hasNextPage, isError, refetch, fetchNextPage, isLoading } =
    useRoadmapAuthorUserIdInfinityQuery({
      userId: String(profile?.id),
      queryObject: {
        pageSize: ROADMAP_ITEM_PAGE_LIMIT,
      },
      options: {
        enabled: Boolean(profile?.id),
      },
    });
  const roadmapTotalCount = data[0]?.totalCount;
  const isEmptyRoadmap = isNil(roadmapTotalCount) || roadmapTotalCount === 0;

  // 페이지 이탈에 대한 이벤트 핸들러 함수
  const handleBeforeUnload = useCallback(() => {
    if (viewRoadmap === STORAGE_VALUE.VIEW_ROADMAP.NO_VIEWED) {
      setViewRoadmap(STORAGE_VALUE.VIEW_ROADMAP.VIEWED);
    }
  }, [viewRoadmap]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      handleBeforeUnload();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  const userName = profile?.name;

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        title={t('{{userName}}님의 로드맵', { userName: userName })}
        description={t(
          '인프런 {{userName}}님의 로드맵 페이지 입니다. - {{userName}}님 소개 | 인프런',
          { userName: userName },
        )}
        image={profile?.thumbnailUrl}
      />

      <SectionHeader>
        <SectionHeader.Title title={t('로드맵')}>
          {isEmptyRoadmap ? null : <SectionHeader.TotalCount totalCount={roadmapTotalCount} />}
        </SectionHeader.Title>
      </SectionHeader>
      <EmptyStates
        height={300}
        onRetry={refetch}
        isError={isError}
        isEmpty={!isLoading && isEmptyRoadmap}
        empty={
          isMe
            ? {
                title: t('제작한 로드맵이 없어요'),
                body: t('나만의 로드맵을 만들어보세요.'),
                cta: (
                  <Button
                    component="a"
                    variant="default"
                    size="sm"
                    radius="md"
                    href={INFLEARN_URL.CREATE_ROADMAP()}
                  >
                    {t('로드맵 만들기')}
                  </Button>
                ),
              }
            : {
                title: t('제작한 로드맵이 없어요'),
              }
        }
      >
        <GridList>
          {isLoading ? (
            <DeferredComponent>
              <RoadmapSkeletonItem count={5} />
            </DeferredComponent>
          ) : (
            data.map((roadmap) =>
              roadmap?.items?.map((item) => (
                <Box key={item.id} component="li">
                  <RoadmapItem id={item.id} title={item.title} thumbnailUrl={item.thumbnailUrl} />
                </Box>
              )),
            )
          )}
          {isFetchingNextPage ? <RoadmapSkeletonItem count={6} /> : null}
          <ImpressionArea
            isImpressed={Boolean(hasNextPage) && !isFetchingNextPage}
            impressionRatio={0.5}
            onImpressionStart={fetchNextPage}
          />
        </GridList>
        {hasNextPage && isError ? <ReFetchInfinityScroll fetchNext={fetchNextPage} /> : null}
      </EmptyStates>
    </Layout>
  );
};

export const getServerSideProps = withProfileUserProps(async ({ context, queryClient }) => {
  const userId = context.params?.id ? String(context.params?.id) : '';

  try {
    await queryClient.fetchInfiniteQuery(
      getUseRoadmapAuthorUserIdInfinityQueryKey({
        userId,
      }),
      () =>
        fetchClientApiV1RoadmapAuthorUserId(
          getUseRoadmapAuthorUserIdQueryUrl({
            userId,
            pageNumber: 1,
            pageSize: ROADMAP_ITEM_PAGE_LIMIT,
          }),
        ),
    );

    return {
      props: {},
    };
  } catch (e) {
    logger.info(`[ERROR in SSR roadmaps:] ${e}`);

    return {
      props: {
        isApiError: true,
      },
    };
  }
});

export default withRemovedPageFallback(UserRoadmapsPage);
